import { useEffect, useState } from "react";
import styled, { css } from "styled-components";
import Lottie from "lottie-react";

import { H2, TextSm } from "components/atoms/text";
import COLORS from "components/atoms/colors";
import InputText from "components/atoms/input/Text";
import Modal from "components/organisms/Modal";
import SelectSort from "components/templates/project/SideBar/SelectSort";
import Detail from "components/templates/project/SideBar/LiveProduct/Detail";
import ModalSearchFilter from "components/templates/project/SideBar/LiveProduct/ModalSearchFilter";
import Searching from "components/templates/project/SideBar/Searching";
import Toast from "components/templates/project/Toast";
import Image from "components/organisms/Image";

import productSort from "datas/productSort";
import getProductParams from "utils/getProductParams";

import useInput from "hooks/useInput";
import useIntersectionObserver from "hooks/useIntersectionObserver";

import useSearchProduct from "apis/mutations/useSearchProduct";
import useProducts from "apis/queries/product/useProducts";

import SearchType from "types/SearchType";
import SceneImageMode from "types/SceneImgMode";

import { ReactComponent as IconReset } from "assets/images/project/reset.svg";
import { ReactComponent as IconSearch } from "assets/images/project/search.svg";
import { ReactComponent as IconKakao } from "assets/images/common/kakao.svg";
import loading from "assets/json/loading.json";
import logoGray from "assets/images/project/logo-gray.png";

interface Props {
  productId: number;
  productOptionId: number;
  pinId: number;
  cropImage: string;
  sceneImageMode: SceneImageMode;
  onChangeProductOptionId: (optionId: number) => void;
  onChangeSceneImageMode: (mode: SceneImageMode) => void;
  onResetPin: () => void;
  onChageProductId: (id: number) => void;
  onResetCropBoxData: () => void;
  searchType: SearchType;
  onSearchType: (type: SearchType) => void;
}

const initialFilter = {
  origin: "",
  // resistPrinting: "",
  locationId: 0,
  purposeId: 0,
  categoryIds: [],
  prices: ["", ""],
  priceLabel: "",
};

// 상품 검색 탭
const Search = ({
  onChangeProductOptionId,
  productOptionId,
  cropImage,
  pinId,
  productId,
  sceneImageMode,
  onChangeSceneImageMode,
  onResetPin,
  onChageProductId,
  onResetCropBoxData,
  searchType,
  onSearchType,
}: Props) => {
  const sorts =
    sceneImageMode === "crop" ? productSort.ai : productSort.general;
  const [sortIndex, setSortIndex] = useState(0);
  const [keyword, setKeyword] = useState("");

  // 검색 필터 상태
  const [filter, dispatchFilter] = useInput(initialFilter);
  const { prices, priceLabel, ...newFilter } = filter;
  const minPrice = prices[0];
  const maxPrice = prices[1];

  // 일반 상품 목록 api
  const productParams = {
    keyword,
    minPrice,
    maxPrice,
    ...newFilter,
    ...sorts[sortIndex].value,
  };
  const newProductParams = getProductParams(productParams);
  const generalProduct = useProducts({
    params: newProductParams,
    enabled: sceneImageMode !== "crop",
  });

  // 무한스크롤 타켓 감지 커스텀 훅
  const { setTarget } = useIntersectionObserver({
    hasNextPage: generalProduct.hasNextPage,
    fetchNextPage: generalProduct.fetchNextPage,
  });

  // 크롭퍼 ai 검색 결과 api
  const searchDto = {
    file: cropImage,
    keyword,
    minPrice,
    maxPrice,
    ...sorts[sortIndex].value,
    ...newFilter,
  };
  const newSearchDto = getProductParams(searchDto);
  const aiSearchProduct = useSearchProduct({
    searchDto: newSearchDto,
    onSuccess: () => onSearchType("list"),
  });

  const aiSearchDependencies = Object.values(searchDto);

  useEffect(() => {
    if (sceneImageMode === "crop") {
      aiSearchProduct.mutate();
    }
  }, aiSearchDependencies);

  // 상품 목록 => ai 모드 or 일반상품 모드에 따른 결과 목록
  const products =
    sceneImageMode === "crop"
      ? aiSearchProduct.products
      : generalProduct.products;

  // ai 검색 버튼 클릭 이벤트
  const handleClickAiSearch = () => {
    onSearchType("searching");
    onChangeSceneImageMode("crop");
  };

  // 상품 아이템 클릭 이벤트
  const handleClickProduct = (productId: number) => {
    onChageProductId(productId);
    onResetPin();
    onSearchType("detail");
  };

  // 상품 상세 닫기 이벤트 => 기획 변경되면서 코드 위치 변경
  // const handleCloseDetail = () => {
  //   if (sceneImageMode === "pin") onChangeSceneImageMode("none");
  //   onSearchType("list");
  //   onResetPin();
  // };

  // ai 검색 중지 이벤트
  const handleAiStop = () => {
    onChangeSceneImageMode("none");
    onResetCropBoxData();
  };

  //검색 필터 체크 검사
  const isFilterCheck = Object.values(filter).some((value) => {
    if (typeof value === "object") {
      if (value.length > 0) return !value.every((v) => !v);
      return value.length > 0;
    }
    return !!value;
  });

  // 상품 상세 컴포넌트 오픈 조건 => 기획 변경되면서 코드 위치 변경
  // const isShowDetail = searchType === "detail" || !!pinId;

  // ai search 로딩 컴포넌트
  if (searchType === "searching") {
    return <Searching isLoading={!aiSearchProduct.products} />;
  }

  return (
    <>
      {/* {isShowDetail && (
        <Detail
          productId={productId}
          productOptionId={productOptionId}
          pinId={pinId}
          onChangeProductOptionId={onChangeProductOptionId}
          onClose={handleCloseDetail}
          onChageSceneModePin={() => onChangeSceneImageMode("pin")}
        />
      )} */}
      <Toast
        content="지정하신 영역에 마감이 인식하지 못했습니다. 다시 지정해 주세요."
        isShow={cropImage === "data:," || aiSearchProduct.isError}
      />
      <div className="flex flex-col h-full">
        <div className="flex-none mt-5">
          <div className="flex justify-between items-center px-5 mb-5">
            <H2 className="text-primary-active">Live product</H2>
            <div className="flex gap-1">
              {isFilterCheck && (
                <button
                  onClick={() => dispatchFilter({ reset: true })}
                  className="flex gap-1 items-center"
                >
                  <IconReset />
                </button>
              )}

              {/* 기획변경 - AI탭 클릭 시 크롭모드 & ai 버튼 숨기기 */}
              {/* {sceneImageMode === "crop" ? (
                <BtnSearch onClick={handleAiStop} red>
                  STOP
                </BtnSearch>
              ) : (
                <BtnSearch onClick={handleClickAiSearch}>AI Search</BtnSearch>
              )} */}
            </div>
          </div>
          <div className="px-5">
            <InputText
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              children={<IconSearch />}
              placeholder="검색어를 입력하세요"
              shape="round"
            />
          </div>
          <div className="flex items-center justify-between px-5 mt-3">
            <Modal
              activator={(onOpen) => (
                <BtnFilter onClick={onOpen} active={isFilterCheck}>
                  검색필터
                </BtnFilter>
              )}
              modal={(onClose) => (
                <ModalSearchFilter
                  onClose={onClose}
                  onChangeFilter={(objectState) =>
                    dispatchFilter({ objectState })
                  }
                  initialFilter={filter}
                />
              )}
            />
            <SelectSort
              options={sorts}
              sortIndex={sortIndex}
              onChageSortIndex={(i) => setSortIndex(i)}
            />
          </div>
        </div>

        {(() => {
          // 로딩 중
          if (!products) {
            return (
              <div className="flex-1 flex flex-col items-center justify-center">
                <Lottie
                  animationData={loading}
                  style={{ width: 140, height: 140 }}
                />
              </div>
            );
          }

          // 상품 개수가 0개일 때
          if (products.length === 0) {
            return (
              <div className="flex-1 flex flex-col justify-center items-center">
                <TextSm className="text-black-40% text-center">
                  앗! 적합한 마감재를 찾을수 없었어요.
                  <br />
                  문의하기를 통해 찾으시는 마감재를
                  <br />
                  알려주시면 빠르게 준비할께요. 😊
                </TextSm>
                <BtnKaKao href="http://pf.kakao.com/_xapljG" target="_blank">
                  <IconKakao />
                  카카오톡 문의하기
                </BtnKaKao>
              </div>
            );
          }

          // 상품 목록
          return (
            <div className="flex-1 overflow-y-auto mt-6">
              {products.map((product) => {
                const { image, subCategory, name, category, score } = product;

                return (
                  <Item
                    key={product.id}
                    onClick={() => handleClickProduct(product.id)}
                  >
                    <ImageWrap>
                      <Image src={image} className="!h-auto" />
                    </ImageWrap>

                    <ul className="flex flex-col gap-y-2 justify-center">
                      {score && (
                        <InfoItem>
                          <InfoTitle>유사도</InfoTitle>
                          <p>{(score * 100).toFixed(2)}%</p>
                        </InfoItem>
                      )}
                      <InfoItem>
                        <InfoTitle>제품명</InfoTitle>
                        <p>{name}</p>
                      </InfoItem>
                      <InfoItem>
                        <InfoTitle>
                          <b>구</b>
                          <b>분</b>
                        </InfoTitle>
                        <p>{category?.name}</p>
                      </InfoItem>
                      <InfoItem>
                        <InfoTitle>
                          <b>종</b>
                          <b>류</b>
                        </InfoTitle>
                        <p>{subCategory}</p>
                      </InfoItem>
                    </ul>
                  </Item>
                );
              })}
              <div ref={setTarget} className="h-5" />
            </div>
          );
        })()}
      </div>
    </>
  );
};

const BtnSearch = styled.button<{ red?: boolean }>`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  padding: 0 12px;
  width: 80px;
  height: 34px;
  background-color: ${({ red }) => (red ? COLORS.red : COLORS.primary.active)};
  border-radius: 19px;
  font-weight: 700;
  color: #fff;
`;

const BtnFilter = styled.button<{ active: boolean }>`
  display: flex;
  align-items: center;
  height: 34px;
  padding: 10px;
  border-radius: 27px;
  background: ${COLORS.primary.active};
  font-weight: 500;
  color: ${COLORS.white["60%"]};

  ${({ active }) =>
    active &&
    css`
      color: ${COLORS.white};
      background: ${COLORS.primary.active};
      font-weight: 700;
    `}
`;

const Item = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  gap: 12px;
  border-bottom: 1px solid ${COLORS.black["20%"]};
  padding: 12px 0;
  margin: 0 20px;
  cursor: pointer;

  &:hover {
    &:after {
      content: "";
      display: block;
      position: absolute;
      z-index: -1;
      width: calc(100% + 40px);
      top: 0;
      left: -20px;
      height: 100%;
      background-color: ${COLORS.white["60%"]};
    }
  }
`;

const ImageWrap = styled.div`
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100px;
  height: 100px;
  border-radius: 6px;
  overflow: hidden;
`;

const InfoItem = styled.li`
  display: flex;
  gap: 8px;

  p {
    letter-spacing: -0.072px;
  }
`;

const InfoTitle = styled.p`
  flex: none;
  color: ${COLORS.black["80%"]};
  width: 31px;
  display: flex;
  justify-content: space-between;

  &,
  b {
    font-weight: 700;
  }
`;

const BtnKaKao = styled.a`
  margin-top: 20px;
  display: flex;
  width: 148px;
  height: 48px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  border-radius: 34px;
  background: #ffdb00;
  font-size: 14px;
  font-weight: 700;
  color: ${COLORS.black["100%"]};
`;

export default Search;
